import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { AlertCircle, CheckCircle, Clock, Loader, PoundSterling, Tag } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import TermsAcceptance from "../components/TermsAcceptance";
import { useConnectedStripe } from "../hooks/useConnectedStripe";
import { apiClient } from "../lib/apiClient";

interface Payment {
	id: string;
	title: string;
	description?: string;
	amount: number;
	currency: string;
	category?: string;
	status: string;
	stripePaymentIntentId: string;
	stripeAccountId: string;
	clientSecret: string;
	createdAt: string;
	expiresAt: string;
	customerId?: string;
}

function PaymentFormContent({ payment }: { payment: Payment }) {
	const stripe = useStripe();
	const elements = useElements();
	const [isProcessing, setIsProcessing] = useState(false);
	const [isAcceptingTerms, setIsAcceptingTerms] = useState(false);
	const [paymentStatus, setPaymentStatus] = useState<"idle" | "success" | "error">("idle");
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [termsAccepted, setTermsAccepted] = useState(false);

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();

		if (!stripe || !elements) {
			return;
		}

		if (!termsAccepted) {
			setErrorMessage("Please accept the terms and conditions to proceed with payment");
			return;
		}

		setIsProcessing(true);
		setIsAcceptingTerms(true);
		setErrorMessage(null);

		try {
			// Accept terms and conditions
			await apiClient().post("/customer/terms_and_conditions/accept-terms", {
				action_string: `/pay/${payment.id}`,
				actor_type: "customer",
				surface: "payment_page",
				consent_method: "click_wrap",
				payment_id: payment.id,
			});

			setIsAcceptingTerms(false);
		} catch (error: unknown) {
			console.error("Failed to accept terms:", error);
		}

		try {
			const { error, paymentIntent } = await stripe.confirmPayment({
				elements,
				confirmParams: {
					return_url: `${window.location.origin}/pay/${payment.id}/success`,
				},
				redirect: "if_required",
			});

			if (error) {
				setErrorMessage(error.message || "Payment failed");
				setPaymentStatus("error");
			} else if (paymentIntent && paymentIntent.status === "succeeded") {
				setPaymentStatus("success");
			}
		} catch (error) {
			console.error("Payment processing error:", error);
			setErrorMessage("An unexpected error occurred during payment processing");
			setPaymentStatus("error");
		}

		setIsProcessing(false);
	};

	const formatAmount = (amount: number) => {
		return `£${(amount / 100).toFixed(2)}`;
	};

	if (paymentStatus === "success") {
		return (
			<div className="text-center py-8 sm:py-12">
				<CheckCircle className="w-14 h-14 sm:w-16 sm:h-16 text-green-500 mx-auto mb-4 sm:mb-6" />
				<h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
					Payment Successful!
				</h2>
				<p className="text-gray-600 mb-3 sm:mb-4 text-sm sm:text-base">
					Thank you for your payment of {formatAmount(payment.amount)}
				</p>
				<p className="text-xs sm:text-sm text-gray-500">
					You should receive a confirmation email shortly.
				</p>
			</div>
		);
	}

	return (
		<form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
			<div>
				<div className="bg-white">
					<PaymentElement
						options={{
							layout: "accordion",
							defaultValues: {
								billingDetails: {
									address: {
										country: "GB",
									},
								},
							},
							wallets: {
								applePay: "auto",
								googlePay: "auto",
							},
						}}
					/>
				</div>
			</div>

			{/* Terms and Conditions */}
			<TermsAcceptance termsAccepted={termsAccepted} onTermsChange={setTermsAccepted} />

			{errorMessage && (
				<div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
					<AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
					<p className="text-red-700 text-sm sm:text-base">{errorMessage}</p>
				</div>
			)}

			<button
				type="submit"
				disabled={!stripe || isProcessing || !termsAccepted}
				className="w-full bg-gradient-to-r from-orange-400 to-pink-400 hover:from-orange-500 hover:to-pink-500 disabled:from-gray-300 disabled:to-gray-300 text-white font-semibold py-4 px-6 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 text-base sm:text-lg touch-manipulation min-h-[56px]"
			>
				{isProcessing ? (
					<>
						<Loader className="w-5 h-5 animate-spin" />
						<span>{isAcceptingTerms ? "Accepting Terms..." : "Processing Payment..."}</span>
					</>
				) : (
					`Pay ${formatAmount(payment.amount)}`
				)}
			</button>
		</form>
	);
}

function PaymentForm({ payment }: { payment: Payment }) {
	const stripePromise = useConnectedStripe(payment.stripeAccountId);

	const formatAmount = (amount: number) => {
		return `£${(amount / 100).toFixed(2)}`;
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("en-GB", {
			day: "numeric",
			month: "short",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	return (
		<div className="space-y-4 sm:space-y-6">
			{/* Payment Details */}
			<div className="bg-gray-50 rounded-lg p-4 sm:p-6">
				<h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 break-words">
					{payment.title}
				</h2>
				{payment.description && (
					<p className="text-gray-600 mb-4 text-sm sm:text-base break-words">
						{payment.description}
					</p>
				)}

				<div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-sm text-gray-500">
					<div className="flex items-center gap-2">
						<PoundSterling className="w-4 h-4 flex-shrink-0" />
						<span className="text-xl sm:text-2xl font-bold text-gray-900">
							{formatAmount(payment.amount)}
						</span>
					</div>
					<div className="flex items-center gap-2">
						<Clock className="w-4 h-4 flex-shrink-0" />
						<span className="text-xs sm:text-sm">Expires {formatDate(payment.expiresAt)}</span>
					</div>
					{payment.category && (
						<div className="flex items-center gap-2">
							<Tag className="w-4 h-4 flex-shrink-0" />
							<span className="capitalize text-xs sm:text-sm">{payment.category}</span>
						</div>
					)}
				</div>
			</div>

			{/* Payment Form */}
			<Elements
				stripe={stripePromise}
				options={{
					clientSecret: payment.clientSecret,
					appearance: {
						theme: "stripe",
						variables: {
							colorPrimary: "#fb923c",
							colorBackground: "#ffffff",
							colorText: "#30323d",
							colorDanger: "#df1b41",
							fontFamily: "Inter, system-ui, sans-serif",
							spacingUnit: "4px",
							borderRadius: "8px",
						},
					},
					locale: "en-GB",
				}}
			>
				<PaymentFormContent payment={payment} />
			</Elements>
		</div>
	);
}

export default function PaymentPage() {
	const params = useParams();
	const paymentId = params.id;

	const [payment, setPayment] = useState<Payment | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!paymentId) return;

		const fetchPayment = async () => {
			try {
				setIsLoading(true);
				const response = await apiClient().get<Payment>(`/payments/${paymentId}`);
				setPayment(response.data);
			} catch (err: unknown) {
				console.error("Failed to fetch payment:", err);
				const error = err as { response?: { status?: number } };
				setError(
					error.response?.status === 404
						? "Payment has been completed or expired"
						: "Failed to load payment details",
				);
			} finally {
				setIsLoading(false);
			}
		};

		fetchPayment();
	}, [paymentId]);

	if (isLoading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50 flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4" />
					<p className="text-gray-600">Loading payment details...</p>
				</div>
			</div>
		);
	}

	if (error || !payment) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50 flex items-center justify-center">
				<div className="text-center p-8">
					<AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
					<h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Not Available</h2>
					<p className="text-gray-600">{error || "This payment link is no longer valid."}</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50">
			<div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
				<div className="max-w-2xl mx-auto">
					{/* Header */}
					<div className="text-center mb-6 sm:mb-8">
						<h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
							Complete Your Payment
						</h1>
						<p className="text-sm sm:text-base text-gray-600">Pay with card, Klarna, or Clearpay</p>
					</div>

					{/* Payment Form */}
					<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
						<PaymentForm payment={payment} />
					</div>

					{/* Payment Methods Info */}
					<div className="text-center mt-4 sm:mt-6">
						<div className="text-xs text-gray-400 space-y-1">
							<p>💳 All major debit and credit cards accepted</p>
							<p>🛍️ Buy now, pay later with Klarna (0% interest available)</p>
							<p>✨ Split into 4 interest-free payments with Clearpay</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
